import { useEffect, useState } from "react";
import api from "../../services/api";
import { Search, Trash2, UserCheck, UserX, Users, Edit2, X, BookOpen, Printer, Download } from "lucide-react";
import toast from "react-hot-toast";
import StudentIdCard from "../../components/StudentIdCard";

const ROLE_STYLES = {
  admin: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  teacher: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  student: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  institute: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[1, 2, 3, 4, 5].map(i => (
        <td key={i} className="px-6 py-4"><div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" /></td>
      ))}
    </tr>
  );
}

export default function InstituteUsers() {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]); // Main catalog
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [addModal, setAddModal] = useState(false);
  const [assignModal, setAssignModal] = useState({ open: false, user: null }); // Manual allocation toggle
  const [assignmentForm, setAssignmentForm] = useState({ courseId: "", amount: 0, mode: "Cash" });
  
  // ID Card State
  const [printStudent, setPrintStudent] = useState(null);
  const [isBulkPrint, setIsBulkPrint] = useState(false);
  
  const [newUser, setNewUser] = useState({ 
    email: "", password: "", role: "student", mobile_no: "",
    institute_name: "", institute_logo: "", institute_website: "" 
  });
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/institute/users"),
      api.get("/institute/courses").catch(() => ({ data: [] }))
    ])
      .then(([usersRes, coursesRes]) => {
        setUsers(usersRes.data);
        setCourses(coursesRes.data);
      })
      .catch(() => toast.error("Failed to synchronize users & courses"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/institute/users", newUser);
      toast.success("New user created!");
      setAddModal(false);
      setNewUser({ 
        email: "", password: "", role: "student", mobile_no: "",
        institute_name: "", institute_logo: "", institute_website: "" 
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Creation failed");
    }
  };

  const handleRoleToggle = async (user) => {
    const next = user.role === "admin" ? "student" : "admin";
    try {
      await api.put(`/admin/users/${user.id}`, { ...user, role: next });
      toast.success(`Role changed to ${next}`);
      load();
    } catch {
      toast.error("Failed to change role");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const { user } = editModal;
    try {
      await api.put(`/admin/users/${user.id}`, { 
         role: user.role,
         mobile_no: user.mobile_no,
         institute_name: user.institute_name,
         institute_logo: user.institute_logo,
         institute_website: user.institute_website
      });
      // Assuming backend supports email update too, if not we just update role
      toast.success("User updated");
      setEditModal({ open: false, user: null });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!assignmentForm.courseId) {
      toast.error("Please select a course to allocate!");
      return;
    }
    try {
      await api.post("/institute/assign-course", {
        user_id: assignModal.user.id,
        course_id: parseInt(assignmentForm.courseId),
        amount_paid: assignmentForm.mode === "Free" ? 0 : parseInt(assignmentForm.amount || 0),
        payment_mode: assignmentForm.mode
      });
      toast.success(`Successfully allocated course to ${assignModal.user.email}!`);
      setAssignModal({ open: false, user: null });
      setAssignmentForm({ courseId: "", amount: 0, mode: "Cash" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign course.");
    }
  };

  

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Users</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{users.length} registered users</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const students = users.filter(u => u.role === "student");
              if (!students.length) return toast.error("No students found to print ID cards.");
              setIsBulkPrint(true);
              setPrintStudent(null);
              setTimeout(() => window.print(), 500);
            }}
            className="px-5 py-2.5 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 text-white rounded-xl font-bold text-sm transition-all flex items-center gap-2"
          >
            <Printer size={16} /> Bulk Print IDs
          </button>
          <button
            onClick={() => setAddModal(true)}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-lg shadow-indigo-600/20 transition-all flex items-center gap-2"
          >
            <Users size={16} /> Add User
          </button>
        </div>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by email or role..."
          className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
        />
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-6 py-3.5 text-left font-semibold">ID</th>
                <th className="px-6 py-3.5 text-left font-semibold">User</th>
                <th className="px-6 py-3.5 text-left font-semibold">Role</th>
                <th className="px-6 py-3.5 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading
                ? [1, 2, 3, 4, 5].map(i => <SkeletonRow key={i} />)
                : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-16 text-center">
                      <Users size={40} className="mx-auto text-slate-300 dark:text-slate-700 mb-3" />
                      <p className="text-slate-500 font-medium">No users found</p>
                    </td>
                  </tr>
                ) : paginated.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4 text-slate-500 dark:text-slate-400 font-mono text-xs">#{user.id}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-extrabold shrink-0">
                          {user.email?.[0]?.toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-800 dark:text-white">{user.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${ROLE_STYLES[user.role] || ROLE_STYLES.student}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditModal({ open: true, user: { ...user } })}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Edit user"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => handleRoleToggle(user)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${user.role === "admin"
                            ? "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
                            : "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 hover:bg-purple-100"
                            }`}
                          title="Toggle role"
                        >
                          {user.role === "admin" ? <UserX size={13} /> : <UserCheck size={13} />}
                        </button>
                        {user.role === 'student' && (
                          <>
                            <button
                              onClick={() => {
                                setAssignModal({ open: true, user });
                                setAssignmentForm({ courseId: "", amount: 0, mode: "Cash" });
                              }}
                              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100"
                              title="Assign Course Manually"
                            >
                              <BookOpen size={12} /> Assign
                            </button>
                            <button
                              onClick={() => {
                                setIsBulkPrint(false);
                                setPrintStudent(user);
                                setTimeout(() => window.print(), 500);
                              }}
                              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 hover:bg-blue-100 transition-colors"
                              title="Print ID Card"
                            >
                              <Printer size={12} /> ID Card
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setConfirmDelete(user)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete user"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t dark:border-slate-800 flex items-center justify-between">
            <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold dark:text-white">Edit User</h3>
              <button onClick={() => setEditModal({ open: false, user: null })} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input
                  type="email"
                  value={editModal.user.email}
                  disabled
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 cursor-not-allowed text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Mobile No</label>
                <input
                  type="text"
                  value={editModal.user.mobile_no || ""}
                  onChange={e => setEditModal(prev => ({ ...prev, user: { ...prev.user, mobile_no: e.target.value } }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Role</label>
                <select
                  value={editModal.user.role}
                  onChange={e => setEditModal(prev => ({ ...prev, user: { ...prev.user, role: e.target.value } }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="institute">Institute / School</option>
                  <option value="admin">Institute</option>
                </select>
              </div>
              {editModal.user.role === 'institute' && (
                <div className="space-y-4 bg-amber-50 dark:bg-amber-900/10 p-4 rounded-xl border border-amber-100 dark:border-amber-900/30 mt-2">
                   <h4 className="text-xs font-black text-amber-700 uppercase tracking-widest">White Label Branding</h4>
                   <div>
                      <label className="block text-xs text-amber-800 font-semibold mb-1">Institute Name</label>
                      <input 
                        type="text" 
                        value={editModal.user.institute_name || ""} 
                        onChange={e => setEditModal(p => ({ ...p, user: { ...p.user, institute_name: e.target.value } }))}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-amber-200 rounded-lg"
                      />
                   </div>
                   <div>
                      <label className="block text-xs text-amber-800 font-semibold mb-1">Logo URL</label>
                      <input 
                        type="text" 
                        value={editModal.user.institute_logo || ""} 
                        onChange={e => setEditModal(p => ({ ...p, user: { ...p.user, institute_logo: e.target.value } }))}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-amber-200 rounded-lg"
                        placeholder="https://..."
                      />
                   </div>
                   <div>
                      <label className="block text-xs text-amber-800 font-semibold mb-1">Website Link</label>
                      <input 
                        type="text" 
                        value={editModal.user.institute_website || ""} 
                        onChange={e => setEditModal(p => ({ ...p, user: { ...p.user, institute_website: e.target.value } }))}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-amber-200 rounded-lg"
                      />
                   </div>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setEditModal({ open: false, user: null })} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors dark:text-white">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold dark:text-white">Add New User</h3>
              <button onClick={() => setAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="name@example.com"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Password</label>
                <input
                  type="password"
                  required
                  value={newUser.password}
                  onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 6 chars"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Mobile No</label>
                <input
                  type="text"
                  value={newUser.mobile_no}
                  onChange={e => setNewUser({ ...newUser, mobile_no: e.target.value })}
                  placeholder="+1 234 567 8900"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">System Role</label>
                <select
                  value={newUser.role}
                  onChange={e => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="institute">Institute / School</option>
                  <option value="admin">Super Institute</option>
                </select>
              </div>
              {newUser.role === 'institute' && (
                <div className="space-y-4 bg-amber-50 dark:bg-amber-900/10 p-4 rounded-xl border border-amber-100 dark:border-amber-900/30 mt-2">
                   <h4 className="text-xs font-black text-amber-700 uppercase tracking-widest">White Label Branding</h4>
                   <div>
                      <label className="block text-xs text-amber-800 font-semibold mb-1">Institute Name</label>
                      <input 
                        type="text" 
                        required
                        value={newUser.institute_name} 
                        onChange={e => setNewUser({ ...newUser, institute_name: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-amber-200 rounded-lg"
                        placeholder="Greenwood High"
                      />
                   </div>
                   <div>
                      <label className="block text-xs text-amber-800 font-semibold mb-1">Logo URL</label>
                      <input 
                        type="text" 
                        value={newUser.institute_logo} 
                        onChange={e => setNewUser({ ...newUser, institute_logo: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-amber-200 rounded-lg"
                        placeholder="https://..."
                      />
                   </div>
                   <div>
                      <label className="block text-xs text-amber-800 font-semibold mb-1">Website Link</label>
                      <input 
                        type="text" 
                        value={newUser.institute_website} 
                        onChange={e => setNewUser({ ...newUser, institute_website: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-amber-200 rounded-lg"
                      />
                   </div>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setAddModal(false)} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-white">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition-all">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Course Assignment Modal */}
      {assignModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800 relative">
            <div className="flex items-center justify-between mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
              <div>
                <h3 className="text-xl font-black dark:text-white tracking-tight">Assign Course</h3>
                <p className="text-xs font-bold text-slate-400 uppercase mt-0.5 truncate max-w-[280px]">For: {assignModal.user.email}</p>
              </div>
              <button onClick={() => setAssignModal({ open: false, user: null })} className="text-slate-400 hover:text-slate-600 dark:hover:text-white bg-slate-50 dark:bg-slate-800 p-2 rounded-full">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAssignSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Select Course</label>
                <select
                  required
                  value={assignmentForm.courseId}
                  onChange={e => setAssignmentForm({ ...assignmentForm, courseId: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">-- Choose Course from Catalog --</option>
                  {courses.map(c => (
                    <option key={c.id} value={c.id}>{c.title} (₹{c.price})</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Payment Mode</label>
                  <select
                    value={assignmentForm.mode}
                    onChange={e => {
                      const mode = e.target.value;
                      setAssignmentForm({ 
                        ...assignmentForm, 
                        mode,
                        amount: mode === 'Free' ? 0 : assignmentForm.amount
                      });
                    }}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="Cash">Cash Collected</option>
                    <option value="Free">Free (₹0)</option>
                    <option value="Online">Online (Manual)</option>
                  </select>
                </div>

                {assignmentForm.mode !== 'Free' && (
                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2">Collected Amount (₹)</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={assignmentForm.amount}
                      onChange={e => setAssignmentForm({ ...assignmentForm, amount: parseInt(e.target.value || 0) })}
                      placeholder="Enter exact rupees"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white text-sm font-black outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button type="button" onClick={() => setAssignModal({ open: false, user: null })} className="flex-1 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-white transition-all">Cancel</button>
                <button type="submit" className="flex-1 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black shadow-lg shadow-emerald-600/20 transition-all">Conclude & Assign</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hidden Print Area for ID Cards */}
      <div className="hidden print:block fixed inset-0 z-[9999] bg-white">
        {isBulkPrint ? (
           <div className="flex flex-col gap-8 items-center pt-8">
             {users.filter(u => u.role === "student").map(u => (
               <StudentIdCard key={u.id} student={u} />
             ))}
           </div>
        ) : printStudent ? (
           <div className="flex items-center justify-center h-full">
             <StudentIdCard student={printStudent} />
           </div>
        ) : null}
      </div>

    </div>
  );
}
